import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';
import type { CoverSheetOverrides } from '../../types';

/**
 * Utility type to recursively convert function types into their return type.
 */
type UnwrapFunction<T> =
  // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
  T extends (...args: any[]) => any ? ReturnType<T> : T;

type UnwrapFunctions<T> = T extends (infer U)[]
  ? UnwrapFunctions<U>[]
  : T extends object
    ? {
        [K in keyof T]: UnwrapFunction<T[K]>;
      }
    : UnwrapFunction<T>;

/**
 * Apply coversheet overrides to an object.
 * @param object The object to apply the overrides to
 * @param overrides Overrides object
 * @returns Base object with overrides applied
 */
export function applyOverrides<
  T extends object,
  K extends CoverSheetOverrides<T>,
>(object: T, overrides?: K) {
  const clonedObject = cloneDeep(object);
  return mergeWith(
    clonedObject,
    overrides,
    (objValue: unknown, srcValue: unknown) => {
      if (typeof srcValue === 'function') return srcValue(objValue);
      if (Array.isArray(srcValue)) return srcValue;
      return undefined;
    },
  ) as T & UnwrapFunctions<K>;
}
